import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import SearchableDropdown from '../../../components/common/SearchableDropdown';

const WarehouseTransferForm = () => {
  const [formData, setFormData] = useState({
    from_warehouse_id: '',
    to_warehouse_id: '',
    transfer_date: '',
    commodity_id: '',
    quantity_mt: '',
    purchase_contract_id: '',
    remarks: '',
    from_cma_id: '',
    to_cma_id: ''
  });

  const [fromWarehouses, setFromWarehouses] = useState([]);
  const [toWarehouses, setToWarehouses] = useState([]);
  const [commodityOptions, setCommodityOptions] = useState([]);
  const [orderOptions, setOrderOptions] = useState([]);
  const [cmaOptions, setCmaOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [commRes, orderRes, cmaRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/masters/dropdown-commodities`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/masters/dropdown-purchase-contract`, { credentials: 'include' }),
          fetch(`${API_BASE_URL}/api/masters/dropdown-cma-list`, { credentials: 'include' })
        ]);

        const [commData, orderData, cmaData] = await Promise.all([
          commRes.json(), orderRes.json(), cmaRes.json()
        ]);

        if (commRes.ok) setCommodityOptions(commData.data || []);
        if (orderRes.ok) setOrderOptions(orderData.data || []);
        if (cmaRes.ok) setCmaOptions(cmaData.data || []);
      } catch (error) {
        console.error('Dropdown load error:', error);
      }
    };

    fetchDropdowns();
  }, []);

  useEffect(() => {
    const fetchFromWarehouses = async () => {
      if (!formData.from_cma_id) {
        setFromWarehouses([]);
        return;
      }
      const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cma?cma_id=${formData.from_cma_id}`, { credentials: 'include' });
      const data = await res.json();
      if (res.ok) setFromWarehouses(data.data || []);
    };
    fetchFromWarehouses();
  }, [formData.from_cma_id]);

  useEffect(() => {
    const fetchToWarehouses = async () => {
      if (!formData.to_cma_id) {
        setToWarehouses([]);
        return;
      }
      const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-warehouse-list-by-cma?cma_id=${formData.to_cma_id}`, { credentials: 'include' });
      const data = await res.json();
      if (res.ok) setToWarehouses(data.data || []);
    };
    fetchToWarehouses();
  }, [formData.to_cma_id]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));

    // Agar Purchase Order select hua toh uski details dhundho
    if (name === 'purchase_contract_id') {
      const found = orderOptions.find(o => String(o.id) === String(value));
      setSelectedOrder(found || null);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const res = await fetch(`${API_BASE_URL}/api/warehouse/warehouse-transfers-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: 'Warehouse Transfer Created Successfully!' });
        setFormData({
          from_warehouse_id: '',
          to_warehouse_id: '',
          transfer_date: '',
          commodity_id: '',
          quantity_mt: '',
          purchase_contract_id: '',
          remarks: '',
          from_cma_id: '',
          to_cma_id: ''
        });
        setSelectedOrder(null);
      } else {
        setMessage({ type: 'error', text: data.error || 'Something went wrong' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server Error' });
    }

    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-3 py-1.5">
        <h2 className="text-[15px] font-bold mb-0 text-left">Warehouse Transfer Entry</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#15803d] border border-[#15803d] px-2 py-0.5 rounded text-[12px] font-medium hover:bg-[#f0fdf4]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 px-3 py-2.5">
          <div>
            <label className="block text-[12px] font-medium mb-1">From CMA</label>
            <SearchableDropdown
              options={cmaOptions.map(cma => ({ id: cma.cma_id, name: cma.name }))}
              value={formData.from_cma_id}
              onChange={value => handleDropdownChange('from_cma_id', value)}
              placeholder="Select From CMA"
              displayKey="name"
              valueKey="id"
              required
            />
          </div>

          <div>
            <label className="block text-[12px] font-medium mb-1">From Warehouse</label>
            <SearchableDropdown
              options={fromWarehouses.map(wh => ({ id: wh.warehouse_id, name: wh.name }))}
              value={formData.from_warehouse_id}
              onChange={value => handleDropdownChange('from_warehouse_id', value)}
              placeholder="Select From Warehouse"
              displayKey="name"
              valueKey="id"
              required
            />
          </div>

          <div>
            <label className="block text-[12px] font-medium mb-1">To CMA</label>
            <SearchableDropdown
              options={cmaOptions.map(cma => ({ id: cma.cma_id, name: cma.name }))}
              value={formData.to_cma_id}
              onChange={value => handleDropdownChange('to_cma_id', value)}
              placeholder="Select To CMA"
              displayKey="name"
              valueKey="id"
              required
            />
          </div>

          <div>
            <label className="block text-[12px] font-medium mb-1">To Warehouse</label>
            <SearchableDropdown
              options={toWarehouses.map(wh => ({ id: wh.warehouse_id, name: wh.name }))}
              value={formData.to_warehouse_id}
              onChange={value => handleDropdownChange('to_warehouse_id', value)}
              placeholder="Select To Warehouse"
              displayKey="name"
              valueKey="id"
              required
            />
          </div>

          <div>
            <label className="block text-[12px] font-medium mb-1">Transfer Date</label>
            <input
              type="date"
              name="transfer_date"
              value={formData.transfer_date}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-[13px]"
              required
            />
          </div>

          <div>
            <label className="block text-[12px] font-medium mb-1">Commodity</label>
            <SearchableDropdown
              options={commodityOptions}
              value={formData.commodity_id}
              onChange={value => handleDropdownChange('commodity_id', value)}
              placeholder="Select Commodity"
              displayKey="name"
              valueKey="commodity_id"
              required
              customDisplay={(item) => `${item.name} (${item.origin || 'N/A'})`}
            />
          </div>

          <div>
            <label className="block text-[12px] font-medium mb-1">Quantity (MT)</label>
            <input
              type="number"
              step="0.001"
              name="quantity_mt"
              value={formData.quantity_mt}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-[13px]"
              required
            />
          </div>

          <div>
            <label className="block text-[12px] font-medium mb-1">Purchase Contract Number</label>
            <SearchableDropdown
              options={orderOptions.map(order => ({ id: order.id, name: `${order.id}, ${order.purchase_contract_no}` }))}
              value={formData.purchase_contract_id}
              onChange={value => handleDropdownChange('purchase_contract_id', value)}
              placeholder="Select Purchase Order"
              displayKey="name"
              valueKey="id"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-[12px] font-medium mb-1">Remarks</label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              className="w-full border border-gray-300 px-3 py-1.5 text-[13px] resize-none"
              rows={3}
            />
          </div>
        </div>

        <div className="px-3 pb-4">
          <button
            type="submit"
            className="w-auto px-3 py-1.5 text-[13px] bg-[#15803d] text-white font-semibold tracking-wide rounded border border-[#15803d] hover:bg-[#166534] transition"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Transfer'}
          </button>
          {message && (
            <div className={`mt-2 text-[13px] ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
              {message.text}
            </div>
          )}
        </div>
      </form>

      {/* Purchase Order Details Table */}
      {selectedOrder && (
        <div className="px-3 pb-4">
          <div className="border border-gray-200 rounded">
            <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
              <h3 className="text-[13px] font-semibold text-gray-700">Purchase Order Details</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="bg-gray-100 text-gray-600">
                    <th className="px-3 py-1.5 text-left font-semibold border-b border-gray-200">Purchase Contract Number</th>
                    <th className="px-3 py-1.5 text-left font-semibold border-b border-gray-200">Contract No.</th>
                    <th className="px-3 py-1.5 text-left font-semibold border-b border-gray-200">Contract Date</th>
                    <th className="px-3 py-1.5 text-left font-semibold border-b border-gray-200">Vessel Name</th>
                    <th className="px-3 py-1.5 text-left font-semibold border-b border-gray-200">Supplier</th>
                    <th className="px-3 py-1.5 text-left font-semibold border-b border-gray-200">Port</th>
                    <th className="px-3 py-1.5 text-left font-semibold border-b border-gray-200">Total Qty (MT)</th>
                    <th className="px-3 py-1.5 text-left font-semibold border-b border-gray-200">Unit Price (INR)</th>
                    <th className="px-3 py-1.5 text-left font-semibold border-b border-gray-200">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-gray-50">
                    <td className="px-3 py-1.5 border-b border-gray-100 text-gray-800">{selectedOrder.id || '-'}</td>
                    <td className="px-3 py-1.5 border-b border-gray-100 text-gray-800">{selectedOrder.purchase_contract_no || '-'}</td>
                    <td className="px-3 py-1.5 border-b border-gray-100 text-gray-800">
                      {selectedOrder.contract_date ? new Date(selectedOrder.contract_date).toLocaleDateString('en-IN') : '-'}
                    </td>
                    <td className="px-3 py-1.5 border-b border-gray-100 text-gray-800">{selectedOrder.vessel_name || '-'}</td>
                    <td className="px-3 py-1.5 border-b border-gray-100 text-gray-800">{selectedOrder.supplier_name || selectedOrder.supplier_id || '-'}</td>
                    <td className="px-3 py-1.5 border-b border-gray-100 text-gray-800">{selectedOrder.port_name || '-'}</td>
                    <td className="px-3 py-1.5 border-b border-gray-100 text-gray-800">{selectedOrder.total_qty_mt || '-'}</td>
                    <td className="px-3 py-1.5 border-b border-gray-100 text-gray-800">
                      {selectedOrder.unit_price_inr ? `₹${Number(selectedOrder.unit_price_inr).toLocaleString('en-IN')}` : '-'}
                    </td>
                    <td className="px-3 py-1.5 border-b border-gray-100">
                      <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${
                        selectedOrder.status === 'active' ? 'bg-green-100 text-green-700' :
                        selectedOrder.status === 'closed' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {selectedOrder.status || '-'}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseTransferForm;
